import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Box,
  Button,
  Grid,
  Heading,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
} from '@tq/ui-components';

import { MobileModal } from 'components';
import { reportState, openReportModalMobileState, reportStartDateState } from 'store';
import { ReportFilter } from 'types';
import { ReportDates, ReportFilters } from '..';
import { useReports } from '../../hooks';
import { UseMutateFunction } from '@tanstack/react-query';

export type MobileReportModalProps = {
  filters: ReportFilter[];
  exportCsv: UseMutateFunction<unknown, unknown, unknown>;
};

export const MobileReportModal: React.FC<MobileReportModalProps> = ({
  filters,
  exportCsv,
}) => {
  const { t } = useTranslation('reports');
  const { name } = useParams();
  const reports = useReports();

  const [report, setReport] = useRecoilState(reportState);
  const hasFromDateValue = useRecoilValue(reportStartDateState);
  const isOpen = useRecoilValue(openReportModalMobileState);
  const resetIsOpen = useResetRecoilState(openReportModalMobileState);

  useEffect(() => {
    if (!report && name) {
      const currentReport = reports.find((x) => x.name === name);
      if (currentReport) {
        setReport(currentReport);
      }
    }
  }, [reports, report, setReport, name]);

  const handleCloseClick = () => {
    resetIsOpen();
  };

  return (
    <MobileModal isOpen={isOpen} onClose={handleCloseClick}>
      <ModalHeader>
        {t('configure_report', 'Configure report')}
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody
        aria-label={t('configure_report_modal', 'Configure report modal')}
        overflowX='hidden'
      >
        {report && (
          <VStack
            alignItems='flex-start'
            spacing={0}
            divider={
              <Box
                w='100vw'
                h='1px'
                bg={COLOURS.gray[300]}
                ml={{ base: '-20px !important', md: '-32px !important' }}
                borderBottomWidth={'0 !important'}
              />
            }
          >
            <Grid pb={6} gridGap={4}>
              <Text>
                {report.hasDateFilter
                  ? t(
                      'refine_report_date_filters',
                      'Please select a date range and any optional filters you would like to refine your report before exporting.'
                    )
                  : t(
                      'refine_report_filters',
                      'Please select any optional filters you would like to refine your report before exporting.'
                    )}
              </Text>
              <Text>
                {t(
                  'export_report_csv',
                  'Your refined report will be exported to a .csv file.'
                )}
              </Text>
            </Grid>

            <Grid py={8} gridGap={1}>
              <Text>{t('selected_report', 'Selected report')}</Text>
              <Heading size='md'>{report.header}</Heading>
            </Grid>

            {report.hasDateFilter && (
              <Box py={8}>
                <ReportDates />
              </Box>
            )}

            <Box py={8} w='100%'>
              <ReportFilters filters={filters} collapsible={false} />
            </Box>
          </VStack>
        )}
      </ModalBody>

      <ModalFooter bg={COLOURS.gray[200]}>
        <Grid gridGap={4} w='100%'>
          <Button
            isFullWidth
            type='submit'
            form='report-dates-form'
            isDisabled={!hasFromDateValue}
            onClick={() => exportCsv({})}
          >
            {t('export', 'Export CSV')}
          </Button>
          <Button isFullWidth variant='tertiary' onClick={handleCloseClick}>
            {t('cancel', 'Cancel')}
          </Button>
        </Grid>
      </ModalFooter>
    </MobileModal>
  );
};
